import { Suspense, useCallback, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { openModal } from '@kcd/app-interface/dist/web2app';
import { BottomSheet, Button } from '@kcd/design-system';
import { trackCreativeClick, trackCreativeView } from '@kcd/event-tracker';

import { getResizeImage } from '~/components/Image';
import { useIntersectionObserver } from '~/hooks/event';
import { useEffectOnce } from '~/hooks/lifecycle';
import { useBusinessIdStore } from '~/store/global/useBusinessIdStore';
import type { OAISchema } from '~/types/openapi';
import { isBetweenDate } from '~/utils/date';

import { usePlacementPopups } from '../queries';

import { CloseIcon } from '~/assets/icons';

const placement = '마켓 홈 바텀시트';

const convertRgba = (rgbaArray: Uint8ClampedArray) => {
  return `rgba(${rgbaArray[0]}, ${rgbaArray[1]}, ${rgbaArray[2]}, ${rgbaArray[3]})`;
};

const BottomSheetItem = ({
  adGroupId,
  creativeId,
  name,
  imageUrl,
  imageLandingUrl,
  buttonText,
  buttonLandingUrl,
  closeButtonText,
}: OAISchema['PlacementPopupResponse']) => {
  const viewedCallback = useCallback(() => {
    trackCreativeView({
      adGroupId,
      creativeId,
      placementName: name || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bottomsheetRef = useIntersectionObserver(viewedCallback);
  const imageRef = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(false);
  const [imageLoad, setImageLoad] = useState<boolean>(false);

  const onClickImage = () => {
    trackCreativeClick({
      adGroupId,
      creativeId,
      placementName: name || '',
    });

    setOpened(false);
    if (imageLandingUrl) {
      openModal({ url: imageLandingUrl });
    }
  };

  const onClickButton = () => {
    trackCreativeClick({
      adGroupId,
      creativeId,
      placementName: name || '',
    });

    setOpened(false);
    if (buttonLandingUrl) {
      openModal({ url: buttonLandingUrl });
    }
  };

  const checkBeforeCloseClick = useCallback(() => {
    const setupDate = localStorage.getItem(`${adGroupId}-${creativeId}`);

    // 설정하지 않은경우엔 팝업 노출
    if (!setupDate) {
      setOpened(true);
      return;
    }

    const baseDate = dayjs(setupDate); // 셋업한 날짜
    const endDate = baseDate.add(1, 'day'); // 셋업한 날짜 + 다음날

    // 오늘은 그만보기 하면, 1일동안 보여주지 않음.
    if (isBetweenDate(baseDate.toString(), endDate.toString())) {
      setOpened(false);
    }
  }, [adGroupId, creativeId]);

  const onClickCloseForDay = () => {
    localStorage.setItem(
      `${adGroupId}-${creativeId}`,
      `${new Date() as unknown as string}`,
    );

    setOpened(false);
  };

  useEffectOnce(() => {
    if (imageUrl) {
      checkBeforeCloseClick();
    }

    const image = new Image();
    image.src = imageUrl || '';
    image.crossOrigin = 'Anonymous';

    image.addEventListener('load', () => {
      const canvas: HTMLCanvasElement = document.createElement('canvas');

      canvas.width = image.width;
      canvas.height = image.height;

      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
      ctx.drawImage(image, 0, 0);

      const imageData = ctx.getImageData(0, 0, 1, 1);

      if (imageRef.current) {
        setImageLoad(true);
        imageRef.current.style.backgroundColor = convertRgba(imageData.data);
      }
    });
  });

  return (
    <BottomSheet
      hideDismiss
      maxHeight={100}
      maxHeightUnit="vh"
      opened={opened && imageLoad}
      onClose={() => setOpened(false)}
      className="!z-20 [&>div:nth-child(2)]:m-auto
      [&>div:nth-child(2)]:max-w-screen-md
      [&>div:nth-child(2)]:p-0 [&>div>div>div]:m-0 [&>div]:overflow-hidden [&>div]:p-0"
    >
      <div ref={bottomsheetRef}>
        <div role="presentation" onClick={onClickImage} ref={imageRef}>
          <img
            src={getResizeImage({
              src: imageUrl!,
              width: 480,
            })}
            alt="바텀시트"
            className="m-auto max-h-[428px] min-h-[260px] w-auto"
            crossOrigin="anonymous"
            fetchPriority="high"
          />
        </div>
        <button
          type="button"
          className="absolute right-[10px] top-[10px] p-8"
          onClick={() => setOpened(false)}
        >
          <CloseIcon className="w-24 [&_path]:fill-gray-600" />
        </button>
      </div>
      <div className="mx-20">
        {buttonText && (
          <Button className="!mt-16 w-full" size="lg" onClick={onClickButton}>
            {buttonText}
          </Button>
        )}
        <div
          role="presentation"
          className="my-16 text-center text-hint typo-caption100-regular"
          onClick={onClickCloseForDay}
        >
          {closeButtonText}
        </div>
      </div>
    </BottomSheet>
  );
};

const BottomSheetContents = () => {
  const { businessId } = useBusinessIdStore();
  const { data, isLoading } = usePlacementPopups(businessId, placement);

  if (!data || isLoading) return <></>;

  return <BottomSheetItem {...data[0]} />;
};

export default function MarketBottomSheet() {
  return (
    <Suspense fallback={null}>
      <BottomSheetContents />
    </Suspense>
  );
}
